import DigiobcanApi, {
  type AdminReportDetail,
} from '../../../../api/DigiobcanApi';
import { useM } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import { BLACK_SWITCH_SX } from './DecisionSwitch';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
  Box,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  report: AdminReportDetail;
  onSuccess: () => Promise<unknown>;
  /**
   * Podoba do hlavičky stránky: vo výške tlačidiel, bez vysvetlenia pod
   * menovkou. To sa presúva do tooltipu - v hlavičke by z jedného prepínača
   * urobilo najvyšší prvok riadku.
   */
  dense?: boolean;
  /**
   * Úradník podnet len vidí - nemá kategóriu, v ktorej by ho riešil.
   *
   * Nesie to stránka, lebo podnet môže mať nálezov viac a stačí jedna vlastná
   * kategória. Drží ho `pruser:report-categories:read-all` (sekretariáty);
   * API taký zápis odmieta 403, takže prepínač by inak sľuboval chybu.
   */
  readOnly?: boolean;
};

/**
 * Prepínač „nevhodné".
 *
 * Na desktope stojí v hlavičke stránky (`dense`) vedľa lehoty a hlasov - to sú
 * tri veci, ktoré platia pre podnet ako celok, a hlavička je jediné miesto na
 * obrazovke, ktoré patrí celému podnetu. Na mobile je v obálke: tamojšia
 * hlavička nesie len titul.
 *
 * Nahrádza pôvodnú dvojicu tlačidiel
 * s potvrdzovacím dialógom. Dôvod je v dizajne: je to **stav podnetu, ktorý
 * treba vidieť**, nie akcia schovaná v pätičke - a keďže sa dá kedykoľvek
 * vrátiť tým istým klikom, potvrdzovanie dialógom bolo trenie bez úžitku.
 *
 * Zapnutý stav je zámerne šrafovaný a nie len farebný: „vidí len úrad a autor"
 * je výnimočný režim a nemá splynúť s ostatnými žltými plochami obrazovky
 * (lehota, prekategorizovanie).
 *
 * Šrafuje sa **dvoma blízkymi žltými** (`light` / `main`), nie krémovou proti
 * žltej: pri veľkom rozdiele jasu preráža pásikovanie cez text a názov stavu
 * sa číta horšie než na plnej ploche. Text je čierny a v plnej sile, nie
 * pritmavená žltá - je to to jediné, čo z toho bloku musí byť čitateľné na
 * prvý pohľad.
 *
 * Prepínač je **čierny** (`BLACK_SWITCH_SX`), nie červený: rovnaké pravidlo ako
 * pri prepínačoch rozhodnutia o náleze, aby červená zostala tlačidlu v pätičke.
 */
const ReportVisibilityToggle: React.FC<Props> = ({
  report,
  onSuccess,
  dense = false,
  readOnly = false,
}) => {
  const { logger } = useLogger();
  const flagged = !!report.explicit;

  /**
   * Archivovaný podnet už nikto nevidí, takže „nevhodné" na ňom nemá čo
   * riadiť - a API zápis nad ním odmieta (`409 REPORT_ARCHIVED`). Prepínač
   * zostáva viditeľný a hlási stav, len sa nedá prepnúť: je to jedna z vecí,
   * ktoré o podnete platia, a archív ich má ukázať tak, ako boli.
   */
  const archived = !!report.archived_at;

  /** Prepínač hlási stav, ale prepnúť sa nedá. Dva rôzne dôvody, jeden zámok. */
  const locked = archived || readOnly;

  const toggleMutation = useM(
    async (explicit: boolean) => {
      await DigiobcanApi.adminUpdateReport(report.id, undefined, { explicit });
      return onSuccess();
    },
    {
      onSuccess: (_res, explicit) => {
        toastSuccess(
          explicit
            ? __('Podnet bol označený ako nevhodný.')
            : __('Označenie podnetu ako nevhodného bolo zrušené.'),
        );
      },
      onError: (error, explicit) => {
        logger.error('Failed to toggle report visibility', {
          action: 'toggleReportVisibility',
          error,
          data: { reportId: report.id, explicit },
        });
        toastError(__('Viditeľnosť podnetu sa nepodarilo zmeniť.'));
      },
    },
  );

  const toggle = () => {
    if (locked) return;

    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }
    toggleMutation.mutate(!flagged);
  };

  const hint = archived
    ? __('Podnet je archivovaný - najprv ho obnovte.')
    : readOnly
      ? __('Podnet riešite v inej kategórii - tento môžete len prezerať.')
      : flagged
        ? __('Vidí len úrad a autor - klik zruší')
        : __('Klik označí ako nevhodné a skryje podnet z verejného zoznamu.');

  // Jeden riadok: ikona, text, prepínač.
  const control = (
    <Stack
      component='label'
      direction='row'
      alignItems='center'
      gap={dense ? 1 : 1.25}
      onClick={toggle}
      sx={{
        px: dense ? 1 : 1.25,
        py: dense ? 0.25 : 1,
        flex: 'none',
        borderRadius: 1.25,
        cursor: locked
          ? 'default'
          : toggleMutation.isPending
            ? 'progress'
            : 'pointer',
        border: (theme) =>
          `1px solid ${flagged ? theme.palette.warning.dark : theme.palette.divider}`,
        ...(flagged
          ? {
              // Šrafovanie, nie plná výplň - plocha musí kričať, ale nesmie sa
              // pomýliť so žltými informačnými blokmi nižšie.
              backgroundImage: (theme) =>
                `repeating-linear-gradient(135deg, ${theme.palette.warning.light} 0 12px, ${theme.palette.warning.main} 12px 24px)`,
            }
          : { backgroundColor: 'grey.50' }),
      }}
    >
      <Box
        sx={{
          width: dense ? 22 : 28,
          height: dense ? 22 : 28,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          borderRadius: 1,
          backgroundColor: flagged ? 'text.primary' : 'neutralSurface',
          color: flagged ? 'warning.light' : 'text.secondary',
        }}
      >
        <PriorityHighIcon sx={{ fontSize: dense ? 15 : 18 }} />
      </Box>

      <Stack sx={dense ? { flex: 'none' } : { flex: 1, minWidth: 0 }}>
        <Typography
          variant='body2'
          fontWeight={700}
          color='text.primary'
          noWrap={dense}
        >
          {flagged ? __('Označené ako nevhodné') : __('Zverejnené')}
        </Typography>
        {/* Na šrafovanej ploche je vysvetlenie v plnej sile, na bielej tiché -
            tam je stav bežný a nemá o seba pýtať pozornosť. */}
        {!dense && (
          <Typography
            variant='caption'
            color={flagged ? 'text.primary' : 'text.secondary'}
          >
            {hint}
          </Typography>
        )}
      </Stack>

      <Switch
        checked={flagged}
        disabled={locked || toggleMutation.isPending}
        size={dense ? 'small' : 'medium'}
        sx={BLACK_SWITCH_SX}
        // Klik obsluhuje celý blok; bez tohto by sa prepnutie spustilo dvakrát.
        onClick={(event) => event.preventDefault()}
      />
    </Stack>
  );

  return dense ? <Tooltip title={hint}>{control}</Tooltip> : control;
};

export default ReportVisibilityToggle;
